import apiClient from './client';
import { API_BASE_URL } from '../config';

export type MarketplaceSource = string;

export interface MarketplaceCatalogItem {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

export interface MarketplaceRecord {
  id: string;
  website?: string | null;
  websiteUrl: string;
  da?: number | null;
  dr?: number | null;
  traffic?: number | null;
  price?: number | null;
  language?: string | null;
  country?: string | null;
  categories?: string | null;
  referringDomains?: number | null;
  outgoingDomains?: number | null;
  authorityScore?: number | null;
  trustFlow?: number | null;
  spamScore?: number | null;
  turnaroundTime?: string | null;
  linkType?: string | null;
  sponsored?: string | null;
  homepage?: string | null;
  keywords?: number | null;
  uploadedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MarketplaceListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  language?: string;
  country?: string;
  categories?: string;
  linkType?: string;
  sponsored?: string;
  daMin?: number | '';
  daMax?: number | '';
  drMin?: number | '';
  drMax?: number | '';
  trafficMin?: number | '';
  trafficMax?: number | '';
  priceMin?: number | '';
  priceMax?: number | '';
}

export interface MarketplaceFilterOptions {
  languages: string[];
  countries: string[];
  linkTypes: string[];
  sponsored: string[];
}

function cleanParams(params: MarketplaceListParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    out[key] = value as string | number;
  });
  return out;
}

export interface MarketplaceImportJobProgress {
  phase?: string;
  percent?: number;
  totalRows?: number;
  processedRows?: number;
  imported?: number;
  skipped?: number;
  elapsedMs?: number;
  etaMs?: number | null;
}

export interface MarketplaceImportJob {
  jobId: string;
  source: MarketplaceSource;
  fileName: string;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  progress?: MarketplaceImportJobProgress;
  error?: string;
  result?: { httpStatus: number; body: any };
  createdAt?: number;
  finishedAt?: number;
}

export const MARKETPLACE_LABELS: Record<string, string> = {
  'link-publisher': 'Link Publisher',
  adsy: 'Adsy',
  bazoom: 'Bazoom'
};

export function marketplaceLabel(source: string): string {
  return MARKETPLACE_LABELS[source] || source;
}

function formatDuration(ms?: number | null): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m <= 0) return `${s}s`;
  return `${m}m ${s}s`;
}

export { formatDuration };

/** Clear user-facing message for marketplace upload / API failures */
export function formatMarketplaceUploadError(err: any, fallback = 'Upload failed'): string {
  const raw = String(
    err?.response?.data?.error?.message ||
      err?.response?.data?.message ||
      err?.message ||
      ''
  ).trim();
  const code = err?.code || err?.response?.status;
  const lower = raw.toLowerCase();

  if (err?.code === 'ECONNABORTED' || lower.includes('timeout')) {
    return 'Upload timed out. For large CSVs, try a smaller file or check your connection and try again.';
  }
  if (!err?.response && (lower.includes('network') || code === 'ERR_NETWORK')) {
    return 'Network error — could not reach the server. Check your connection and try again.';
  }
  if (lower.includes('file size') || lower.includes('100mb') || lower.includes('too large')) {
    return 'File is too large. Maximum upload size is 100 MB. Compress or split the CSV and try again.';
  }
  if (lower.includes('only csv') || lower.includes('csv files are allowed')) {
    return 'Only CSV files (.csv) are allowed. Please convert your file to CSV and upload again.';
  }
  if (lower.includes('csv file is required')) {
    return 'Please select a CSV file to upload.';
  }
  if (lower.includes('unexpected field')) {
    return 'Upload form was invalid. Refresh the page and try uploading again.';
  }
  if (lower.includes('header') || lower.includes('column') || lower.includes('csv format')) {
    return raw;
  }
  if (raw) return raw;
  if (err?.response?.status === 413) {
    return 'File is too large for the server. Maximum upload size is 100 MB.';
  }
  if (err?.response?.status === 400) {
    return 'Upload rejected. Check that the file is a valid CSV under 100 MB.';
  }
  return fallback;
}

export const marketplaceApi = {
  listCatalog: async (): Promise<MarketplaceCatalogItem[]> => {
    const response = await apiClient.get('/marketplace/catalog');
    return response.data.data || [];
  },

  createCatalog: async (name: string): Promise<MarketplaceCatalogItem> => {
    const response = await apiClient.post('/marketplace/catalog', { name });
    return response.data.data;
  },

  renameCatalog: async (slug: string, name: string): Promise<MarketplaceCatalogItem> => {
    const response = await apiClient.patch(`/marketplace/catalog/${encodeURIComponent(slug)}`, { name });
    return response.data.data;
  },

  deleteCatalog: async (slug: string): Promise<void> => {
    await apiClient.delete(`/marketplace/catalog/${encodeURIComponent(slug)}`);
  },

  list: async (source: MarketplaceSource, params: MarketplaceListParams = {}) => {
    const response = await apiClient.get(`/marketplace/${source}`, {
      params: cleanParams(params),
      timeout: 120000
    });
    return response.data;
  },

  filterOptions: async (source: MarketplaceSource): Promise<MarketplaceFilterOptions> => {
    const response = await apiClient.get(`/marketplace/${source}/filter-options`);
    return response.data.data;
  },

  upload: async (source: MarketplaceSource, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/marketplace/${source}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    });
    return response.data;
  },

  getActiveJobs: async (): Promise<MarketplaceImportJob[]> => {
    const response = await apiClient.get('/marketplace/jobs/active');
    return response.data.data || [];
  },

  getJobStatus: async (jobId: string): Promise<MarketplaceImportJob> => {
    const response = await apiClient.get(`/marketplace/jobs/${jobId}`);
    return response.data.data;
  },

  cancelJob: async (jobId: string) => {
    const response = await apiClient.post(`/marketplace/jobs/${jobId}/cancel`);
    return response.data;
  },

  export: async (
    source: MarketplaceSource,
    body: { ids?: string[]; exportAll?: boolean } & MarketplaceListParams,
    onProgress?: (info: {
      percent: number;
      phase: string;
      processedRows?: number;
      totalRows?: number;
    }) => void
  ) => {
    const token = localStorage.getItem('token');
    const startRes = await fetch(`${API_BASE_URL}/marketplace/${source}/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });

    if (!startRes.ok) {
      const err = await startRes.json().catch(() => ({}));
      throw new Error(err.message || err.error?.message || 'Export failed');
    }

    const startJson = await startRes.json();
    const jobId = startJson?.data?.jobId as string | undefined;
    if (!jobId) {
      throw new Error('Export job was not created');
    }

    // Poll until export job finishes, then download CSV
    const pollMs = 500;
    for (;;) {
      const statusRes = await fetch(`${API_BASE_URL}/marketplace/export-jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!statusRes.ok) {
        const err = await statusRes.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to get export status');
      }
      const { data: job } = await statusRes.json();
      const percent = typeof job?.progress?.percent === 'number' ? job.progress.percent : 0;
      const phase = job?.progress?.phase || 'Exporting…';
      onProgress?.({
        percent,
        phase,
        processedRows: job?.progress?.processedRows,
        totalRows: job?.progress?.totalRows
      });

      if (job.status === 'COMPLETED') {
        const dlRes = await fetch(`${API_BASE_URL}/marketplace/export-jobs/${jobId}/download`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!dlRes.ok) {
          const err = await dlRes.json().catch(() => ({}));
          throw new Error(err.message || 'Failed to download export');
        }
        const blob = await dlRes.blob();
        const count = dlRes.headers.get('X-Export-Count') || String(job.count || '');
        return { blob, count };
      }

      if (job.status === 'FAILED') {
        throw new Error(job.error || 'Export failed');
      }
      if (job.status === 'CANCELLED') {
        throw new Error('Export cancelled');
      }

      await new Promise((r) => setTimeout(r, pollMs));
    }
  },

  bulkDelete: async (source: MarketplaceSource, ids: string[]) => {
    const response = await apiClient.post(`/marketplace/${source}/bulk-delete`, { ids });
    return response.data;
  },

  downloadTemplate: async (source: MarketplaceSource) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/marketplace/${source}/template`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to download template');
    }
    return response.blob();
  }
};
